<template>
  <div class="rd-screen">
    <report-tools :copy="hasRows" @copy="copyTable">
      <ui-segment
        v-if="hasBranches"
        v-model="view"
        tabs
        :options="viewOptions"
        :label="$t('report_mobile.group_by')"
      />
    </report-tools>

    <report-state v-if="busy" kind="loading"></report-state>
    <report-state v-else-if="!hasRows" icon="mdi-bowl-mix-outline" :title="emptyTitle" :text="emptyText"></report-state>

    <ui-table
      v-else-if="!byBranch"
      variant="report"
      :columns="columns"
      :rows="rows"
      :row-key="(row) => row.key"
      :sort="sort"
      :totals="footTotals"
      :total-label="$t('total')"
      @sort="sortBy"
    >
      <template #head-shift="{ column }">
        <report-shift-tag :shift="column.shift.id"></report-shift-tag>
      </template>

      <template #cell-total="{ row }"><b>{{ row.total }}</b></template>
    </ui-table>

    <template v-else>
      <section v-for="section in branchSections" :key="section.key" class="mr-company">
        <div class="rd-section mr-company__head">
          <div class="mr-company__title">
            <h2>{{ section.name }}</h2>
          </div>
          <span class="rd-num">{{ section.summary }}</span>
        </div>

        <ui-table
          variant="report"
          :columns="columns"
          :rows="section.rows"
          :row-key="(row) => row.key"
          :sort="sort"
          :totals="section.totals"
          :total-label="$t('total')"
          @sort="sortBy"
        >
          <template #head-shift="{ column }">
            <report-shift-tag :shift="column.shift.id"></report-shift-tag>
          </template>

          <template #cell-total="{ row }"><b>{{ row.total }}</b></template>
        </ui-table>
      </section>
    </template>
  </div>
</template>

<script>
import CompanyMultiRestaurantTabMeals from "@/components/company/reports/multi-restaurant-tabs/CompanyMultiRestaurantTabMeals.vue";
import { SHIFTS } from "@/constants/shifts";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import hasPlural from "@/components/report/shared/hasPlural";
import hasTableSort from "@/components/report/desktop/hasTableSort";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import hasMultiFeed from "../../../hasMultiFeed";

/**
 * Obroci firme "više restorana" na računaru: obrok × smena (kupci + restoran, "5 + 2R" = 5 kupaca i 2 restoranu),
 * zbir za izabrane restorane. Kad firma ima više poslovnica, prikaz je zbirni (podrazumevano) ili po poslovnici
 * (tabela za svaku). Restorani, poslovnice, smene, period i pretragu čita iz zajedničke trake. Nasleđuje
 * učitavanje, izbor prikaza, štampu i Excel iz starog taba (štampa i Excel prate prikaz: po poslovnici dodaje kolonu
 * poslovnice).
 */
export default {
  name: "CompanyDesktopMultiMeals",
  extends: CompanyMultiRestaurantTabMeals,
  mixins: [hasMultiFeed, hasTableSort, hasPlural],
  components: { ReportShiftTag, ReportState, ReportTools },
  data: () => ({
    sort: { key: "total", dir: -1 },
  }),
  computed: {
    /** Kolone tabele: iste služe za zaglavlje, redove i zbirni red. */
    columns() {
      return [
        { key: "#", label: "#", index: true },
        { key: "name", label: this.$t("meal"), name: true, sortable: true },
        { key: "type", label: this.$t("type"), muted: true, value: (row) => row.type || "–" },
        ...this.visibleShifts.map((shift) => ({
          key: shift.key,
          slot: "shift",
          shift,
          align: "end",
          num: true,
          sortable: true,
          value: (row) => row.text[shift.key],
          zero: (row) => !row[shift.key],
        })),
        { key: "total", label: this.$t("total"), align: "end", num: true, sortable: true },
      ];
    },
    /** Zbirni red: smene kao tekst (sums.text) i ukupno. */
    footTotals() {
      const totals = { total: this.sums.total };
      this.visibleShifts.forEach((shift) => {
        totals[shift.key] = this.sums.text[shift.key];
      });
      return totals;
    },
    busy() {
      return this.isLoading;
    },
    hasRows() {
      return this.byBranch ? this.branchSections.length > 0 : this.rows.length > 0;
    },
    /** Izabrane smene; četvrta samo kad neki obrok u njoj ima količinu. */
    visibleShifts() {
      return SHIFTS.filter(
        (shift) =>
          this.shiftsWithData.includes(shift.id) &&
          (shift.id < 4 || this.meals.some((meal) => (meal[shift.key] && meal[shift.key].totalQuantity) > 0))
      );
    },
    /** Obroci posle pretrage (naziv ili tip). */
    found() {
      const search = this.$store.state.report.filters.search;
      return search ? this.customSearch(this.meals, [{ value: "name" }, { value: "type" }], search) : this.meals;
    },
    rows() {
      return this.sortRows(this.toRows(this.found));
    },
    sums() {
      return this.sumsOf(this.found);
    },
    /** Po poslovnici: obroci svake poslovnice (posle pretrage), svaka sa svojim zbirom. */
    branchSections() {
      const search = this.$store.state.report.filters.search;
      return this.branchGroups
        .map(({ name, meals }) => {
          const found = search ? this.customSearch(meals, [{ value: "name" }, { value: "type" }], search) : meals;
          const sums = this.sumsOf(found);
          const totals = { total: sums.total };
          this.visibleShifts.forEach((shift) => {
            totals[shift.key] = sums.text[shift.key];
          });
          return {
            key: name || "-",
            name: this.toLatin(name),
            meals: found,
            rows: this.sortRows(this.toRows(found)),
            totals,
            summary: this.pluralT("report_mobile.meals_n", sums.total),
          };
        })
        .filter((section) => section.rows.length);
    },
    emptyTitle() {
      const search = this.$store.state.report.filters.search;
      return search ? this.$t("report_mobile.no_results_for", { search }) : this.$t("report_mobile.no_meals");
    },
    emptyText() {
      return this.$store.state.report.filters.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        { key: "total", label: this.$t("report_desktop.total_meals"), value: this.sums.total, tone: "inverse" },
        { key: "meals", label: this.$t("meals"), value: this.rows.length },
      ];
    },
  },
  methods: {
    /** Redovi tabele za obroke (jedan red po obroku, smene kao tekst "5 + 2R"). */
    toRows(meals) {
      return meals.map((meal, index) => {
        const row = {
          key: `${meal.id || meal.name}-${index}`,
          name: meal.name,
          type: this.typeText(meal.type),
          total: meal.total || 0,
          text: {},
        };
        SHIFTS.forEach((shift) => {
          row[shift.key] = (meal[shift.key] && meal[shift.key].totalQuantity) || 0;
          row.text[shift.key] = this.formatShiftQuantity(meal[shift.key]);
        });
        return row;
      });
    },
    /** Zbir obroka po smenama (kao tekst) i ukupno. */
    sumsOf(meals) {
      const sums = { total: 0, text: {} };
      SHIFTS.forEach((shift) => {
        const sum = { customerQuantity: 0, restaurantQuantity: 0, totalQuantity: 0 };
        meals.forEach((meal) => {
          const data = meal[shift.key] || {};
          sum.customerQuantity += data.customerQuantity || 0;
          sum.restaurantQuantity += data.restaurantQuantity || 0;
          sum.totalQuantity += data.totalQuantity || 0;
        });
        sums.text[shift.key] = this.formatShiftQuantity(sum);
      });
      sums.total = meals.reduce((sum, meal) => sum + (meal.total || 0), 0);
      return sums;
    },
    async copyTable() {
      const shiftLabels = this.visibleShifts.map((shift) => this.$t(shift.label));
      const cells = (row) => [row.name, row.type, ...this.visibleShifts.map((shift) => row.text[shift.key]), row.total];
      const headers = this.byBranch
        ? [this.$t("branch"), this.$t("meal"), this.$t("type"), ...shiftLabels, this.$t("total")]
        : [this.$t("meal"), this.$t("type"), ...shiftLabels, this.$t("total")];
      const rows = this.byBranch
        ? this.branchSections.flatMap((section) => section.rows.map((row) => [section.name, ...cells(row)]))
        : this.rows.map(cells);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
